<script setup lang="ts">
import { computed, onBeforeUnmount, ref, shallowRef, useId, watch } from 'vue'
import { useRouter } from 'vue-router'
import { AlertTriangle, ArrowRight, ChevronDown, RefreshCw, ScanLine } from 'lucide-vue-next'
import { widgetCopy } from '@/i18n/widget-copy'
import { buildInfo } from '@/services/build-info'
import { useI18n } from 'vue-i18n'
import SessionStartIssuePreview from '@/components/SessionStartIssuePreview.vue'
import LevelOcrFailure from '@/components/LevelOcrFailure.vue'
import { useLiveStore, type SessionStartProgress } from '@/stores/live'
import type { AdaptiveMetricRoi, OcrCompletionSnapshot } from '@/domain/types'
import type { SessionStartIssue } from '@/core/session-start-baseline'

const { t, locale } = useI18n()
const emit = defineEmits<{ 'visibility-change': [occupied: boolean] }>()
const router = useRouter()
const live = useLiveStore()
const presentation = shallowRef<{ issue: SessionStartIssue | null; progress: SessionStartProgress | null }>({ issue: null, progress: null })
const visible = computed(() => Boolean(live.startIssue || live.sessionStartProgress?.phase === 'slow'))
// Keep the last visible content during the dialog's closing animation.
watch([() => live.startIssue, () => live.sessionStartProgress], ([issue, progress]) => {
  if (issue || progress?.phase === 'slow') presentation.value = { issue, progress: issue ? null : progress }
}, { immediate: true, flush: 'sync' })
const issue = computed(() => presentation.value.issue)
const slowProgress = computed(() => presentation.value.progress)
const detailsId = useId()
const detailsOpen = ref(false)
const detailsRevision = ref(0)
const detailsSnapshot = shallowRef<{
  progress: SessionStartProgress
  completion?: OcrCompletionSnapshot
  roi?: AdaptiveMetricRoi
  presetId?: string
  presetVersion?: number
} | null>(null)
const detailProgress = computed(() => detailsSnapshot.value?.progress)
watch([() => live.sessionStartProgress?.requestedAt, () => live.startIssue], ([requestedAt, issue], [previousAt]) => {
  if (issue || requestedAt !== undefined && requestedAt !== previousAt) resetDetails()
}, { flush: 'sync' })

function resetDetails(): void {
  detailsOpen.value = false
  detailsSnapshot.value = null
}

function onClosed(): void {
  if (visible.value) return
  resetDetails()
  presentation.value = { issue: null, progress: null }
  emit('visibility-change', false)
}

function refreshDetails(): void {
  const progress = slowProgress.value
  if (!progress) return
  const diagnostic = live.rawReading?.diagnostics.metrics?.[progress.currentMetric]
  const completion = diagnostic?.completion
  const fresh = completion && completion.capturedAt >= progress.requestedAt
  // OCR snapshots own their pixels; retain this attempt until an explicit refresh.
  detailsSnapshot.value = { progress: { ...progress }, completion: fresh ? completion : undefined,
    roi: fresh ? diagnostic?.roi : undefined, presetId: live.preset?.id, presetVersion: live.preset?.version }
  detailsRevision.value++
}

function toggleDetails(): void {
  detailsOpen.value = !detailsOpen.value
  if (detailsOpen.value) refreshDetails()
}

watch(visible, (showing) => {
  if (showing) emit('visibility-change', true)
}, { immediate: true, flush: 'sync' })
onBeforeUnmount(() => emit('visibility-change', false))
const metricLabel = computed(() => issue.value?.metric ? t(`live.${issue.value.metric}`) : '')
const latestCompletion = computed(() => {
  const progress = slowProgress.value
  const completion = progress && live.rawReading?.diagnostics.metrics?.[progress.currentMetric]?.completion
  return completion && completion.capturedAt >= progress!.requestedAt ? completion : undefined
})
const waitingTitle = computed(() => {
  const progress = slowProgress.value, completion = latestCompletion.value
  if (progress?.stage === 'waiting-frame') return widgetCopy(locale.value, 'Waiting for a new game frame', '等待新的游戏画面')
  if (completion && !['ready', 'confirming', 'queued', 'running'].includes(completion.status)) return widgetCopy(locale.value, 'Complete EXP could not be read; retrying', '完整经验解析失败，正在重试')
  if (progress?.confirmedSamples === 1 && !progress.confirmationFailureReason) return widgetCopy(locale.value, 'Waiting for the second confirmation', '等待第二次经验确认')
  return widgetCopy(locale.value, 'Recognizing EXP', '正在识别经验')
})
const waitingReason = computed(() => {
  const progress = slowProgress.value, completion = latestCompletion.value
  if (progress?.stage === 'waiting-frame') return t('live.startFrameSlowHint')
  if (completion && ['parse-failed', 'no-text', 'low-confidence', 'no-result'].includes(completion.status)) {
    const failure = completion.status === 'low-confidence' ? 'low-confidence' : completion.status === 'no-text' ? 'no-text' : 'incomplete'
    return t(`live.startIssue.reasons.${failure}`, { metric: t('live.exp') })
  }
  if (progress?.confirmationFailureReason) return t(`live.expConfirmationFailure.${progress.confirmationFailureReason}`)
  return progress?.confirmedSamples === 1 ? t('live.startIssue.expOneMore') : t('live.startIssue.slowWaitingReason')
})
// Fill an initially empty details panel as soon as its first completed input arrives.
watch(latestCompletion, completion => {
  if (detailsOpen.value && !detailsSnapshot.value?.completion?.input && completion?.input) refreshDetails()
})
const title = computed(() => {
  if (slowProgress.value) return waitingTitle.value
  if (issue.value?.reason === 'capture-unavailable') return t('live.startIssue.captureTitle')
  if (issue.value?.reason === 'engine-error') return t('live.startIssue.engineTitle')
  if (issue.value?.reason === 'slow' || issue.value?.reason === 'unresponsive') return t('live.startIssue.slowTitle')
  return t(issue.value?.metric === 'level' ? 'live.startIssue.levelTitle' : 'live.startIssue.expTitle')
})
const reason = computed(() => issue.value
  ? issue.value.reason === 'preprocess-rejected'
    ? preprocessRejectedLabel.value
    : expFailure.value
    ? t(`live.startIssue.reasons.${expFailure.value}`, { metric: metricLabel.value })
    : issue.value.metric === 'exp' && issue.value.confirmationFailureReason
    ? t(`live.expConfirmationFailure.${issue.value.confirmationFailureReason}`)
    : issue.value.reason === 'confirming'
    && issue.value.metric === 'exp'
    && issue.value.confirmedSamples === 1
    ? t('live.startIssue.expOneMore')
    : issue.value.reason === 'confirming'
    && issue.value.metric === 'level'
    && typeof issue.value.candidate === 'number'
    ? t('live.startIssue.levelConfirming', { candidate: issue.value.candidate })
    : t(`live.startIssue.reasons.${issue.value.reason}`, {
      metric: metricLabel.value,
      candidate: issue.value.candidate ?? '--',
    })
  : detailProgress.value
    ? detailProgress.value.slowReason === 'frame'
      ? t('live.startFrameSlowHint')
      : expFailure.value
      ? t(`live.startIssue.reasons.${expFailure.value}`, { metric: t('live.exp') })
      : detailProgress.value.currentMetric === 'exp' && detailProgress.value.confirmationFailureReason
      ? t(`live.expConfirmationFailure.${detailProgress.value.confirmationFailureReason}`)
      : detailProgress.value.confirmedSamples === 1
      ? detailProgress.value.currentMetric === 'level' && typeof detailProgress.value.candidate === 'number'
        ? t('live.startIssue.levelConfirming', { candidate: detailProgress.value.candidate })
        : t('live.startIssue.expOneMore')
      : t('live.startIssue.slowWaitingReason')
    : '')
const expCompletion = computed(() => {
  if (issue.value) return issue.value.metric === 'exp' ? issue.value.completion : undefined
  return detailProgress.value?.currentMetric === 'exp' ? detailsSnapshot.value?.completion : undefined
})
const previewRoi = computed(() => issue.value?.roi ?? detailsSnapshot.value?.roi)
const previewInput = computed(() => issue.value?.completion?.input ?? detailsSnapshot.value?.completion?.input)
const evidenceText = computed(() => issue.value?.rawText ?? issue.value?.completion?.rawText ?? detailsSnapshot.value?.completion?.rawText)
const expStages = computed(() => widgetCopy(locale.value,
  { primary: 'Full row', brackets: 'Bracket boundaries', 'split-integer': 'EXP number', 'split-percentage': 'Percentage' },
  { primary: '完整文字行', brackets: '括号边界', 'split-integer': '经验数值分区', 'split-percentage': '百分比分区' }))
const expFailure = computed(() => {
  const completion = expCompletion.value
  if (!completion || completion.status === 'ready' || completion.status === 'confirming') return undefined
  return completion.status === 'low-confidence' ? 'low-confidence'
    : completion.status === 'no-text' ? 'no-text' : 'incomplete'
})
const reviewFailureText = computed(() => {
  const failure = issue.value?.reviewFailureReason
    ?? (detailProgress.value?.currentMetric === 'level' ? detailProgress.value.reviewFailureReason : undefined)
  return failure ? t(`live.startIssue.reviewFailure.${failure}`) : ''
})
const levelCompletion = computed(() => {
  if (issue.value) return issue.value.metric === 'level' ? issue.value.completion : undefined
  return detailProgress.value?.currentMetric === 'level' ? detailsSnapshot.value?.completion : undefined
})
const preprocessRejectedLabel = computed(() => t((issue.value ? live.preset?.id : detailsSnapshot.value?.presetId) === 'worlds'
  && (issue.value?.metric ?? detailProgress.value?.currentMetric) === 'level'
  && levelCompletion.value?.input?.method !== 'manual-smooth'
  ? 'ocrQuality.worldsLevelPreprocessRejected' : 'ocrQuality.preprocessRejected'))

function adjust(): void {
  const metric = issue.value?.metric ?? slowProgress.value?.currentMetric
  if (!metric) return
  if (slowProgress.value) live.cancelSessionStart()
  live.dismissStartIssue()
  void router.push({ name: 'calibration', query: { metric, source: 'start' } })
}

function retry(): void {
  void live.startSession()
}

function reconnect(): void {
  live.dismissStartIssue()
  void live.selectWindow()
}
</script>

<template>
  <el-dialog
    :model-value="visible"
    class="start-issue-dialog"
    modal-class="start-issue-overlay"
    transition="start-issue-motion"
    width="31rem"
    align-center
    append-to-body
    destroy-on-close
    lock-scroll
    trap-focus
    :role="slowProgress ? 'dialog' : 'alertdialog'"
    :close-on-click-modal="false"
    :close-on-press-escape="!live.isStarting"
    :show-close="!live.isStarting"
    @close="live.dismissStartIssue"
    @closed="onClosed"
  >
    <template #header="{ titleId, titleClass }">
      <div :key="slowProgress ? 'waiting' : issue?.reason" class="issue-heading" :class="{ 'is-waiting': slowProgress }">
        <span class="issue-icon" :class="{ loading: slowProgress }" aria-hidden="true">
          <ScanLine v-if="slowProgress" />
          <AlertTriangle v-else />
        </span>
        <div><span class="issue-eyebrow">{{ t(slowProgress ? 'live.startIssue.waitingEyebrow' : 'live.startIssue.eyebrow') }}</span><h2 :id="titleId" :class="titleClass">{{ title }}</h2></div>
      </div>
    </template>

    <p class="issue-reason" :role="slowProgress ? 'status' : 'alert'">{{ slowProgress ? waitingReason : reason }}</p>
    <div v-if="slowProgress" class="confirmation-progress" role="status" aria-live="polite" aria-atomic="true">
      <span class="sr-only">{{ t('live.startIssue.confirmationProgress', { metric: t(`live.${slowProgress.currentMetric}`), confirmed: slowProgress.confirmedSamples, target: slowProgress.targetSamples }) }}</span>
      <div class="confirmation-summary" aria-hidden="true">
        <span>{{ t('live.startIssue.confirmationLabel', { metric: t(`live.${slowProgress.currentMetric}`) }) }}</span>
        <span class="confirmation-count"><strong :key="`${slowProgress.currentMetric}-${slowProgress.confirmedSamples}`">{{ slowProgress.confirmedSamples }}</strong><span> / {{ slowProgress.targetSamples }}</span></span>
      </div>
      <div class="confirmation-steps" aria-hidden="true"><span v-for="step in slowProgress.targetSamples" :key="step" :class="{ complete: step <= slowProgress.confirmedSamples }"><i /></span></div>
    </div>

    <Transition name="issue-disclosure">
      <div v-if="!slowProgress || detailsOpen" :id="slowProgress ? detailsId : undefined" class="issue-details-reveal">
        <div class="issue-details-clip">
          <div :class="{ 'issue-details': slowProgress }">
            <div v-if="slowProgress" class="issue-details-toolbar"><span>{{ t('live.startIssue.details') }}</span><el-button text size="small" @click="refreshDetails"><RefreshCw :key="detailsRevision" class="details-refresh-icon" aria-hidden="true" />{{ t('live.startIssue.refreshDetails') }}</el-button></div>
            <div :key="detailsRevision" class="issue-diagnostics">
              <p v-if="slowProgress" class="issue-reason">{{ reason }}</p>
              <p class="recognition-path">{{ widgetCopy(locale, 'Recognition preset / build:', '识别预设 / 应用版本：') }} {{ (issue ? live.preset?.id : detailsSnapshot?.presetId)?.toUpperCase() ?? '—' }} v{{ (issue ? live.preset?.version : detailsSnapshot?.presetVersion) ?? '—' }} · {{ buildInfo.revision }}</p>
              <p v-if="expFailure && (issue?.confirmedSamples ?? detailProgress?.confirmedSamples) === 1" class="review-note" role="status">{{ t('live.startIssue.expProgressRetained') }}</p>
              <LevelOcrFailure :completion="levelCompletion" :retrying="!issue && Boolean(slowProgress)" :preprocess-rejected-label="preprocessRejectedLabel" />
              <p v-if="reviewFailureText" class="review-note" role="status">{{ reviewFailureText }}</p>
              <SessionStartIssuePreview v-if="previewRoi && previewInput" :snapshot="previewInput" :video="live.video" :roi="previewRoi" :text-label="t('calibration.actualTextRange')" :bar-label="t('calibration.actualBarRange')" />
              <p v-else-if="slowProgress" class="issue-preview-empty">{{ t('live.startIssue.noSnapshot') }}</p>
              <p v-if="previewInput && (issue?.completion ?? detailsSnapshot?.completion)" class="recognition-path">{{ widgetCopy(locale, 'Source frame:', '来源帧：') }} {{ new Date((issue?.completion ?? detailsSnapshot!.completion)!.capturedAt).toLocaleTimeString(locale, { hour12: false, fractionalSecondDigits: 3 }) }}</p>
              <p v-if="previewInput && (issue ? live.preset?.id : detailsSnapshot?.presetId) === 'gcw'" class="recognition-path">{{ widgetCopy(locale, 'Preview: full-row input; split retries appear below.', '预览为完整文字行输入，分区重读原文见下方。') }}</p>
              <div v-if="evidenceText" class="ocr-evidence"><span>{{ t('live.startIssue.detectedText') }}</span><code>{{ evidenceText }}</code></div>
              <div v-for="(attempt, index) in expCompletion?.attempts" :key="index" class="ocr-evidence exp-attempt">
                <span>{{ attempt.stage && attempt.stage in expStages ? expStages[attempt.stage as keyof typeof expStages] : `${t('live.exp')} ${index + 1}` }} · {{ t(`calibration.status.${attempt.status}`) }}</span><code>{{ attempt.rawText || '—' }}</code>
              </div>
              <p v-if="issue?.recognitionPath" class="recognition-path">{{ t(`live.startIssue.path.${issue.recognitionPath}`) }}</p>
              <p v-if="issue?.metric" class="issue-guidance"><ScanLine aria-hidden="true" /><span>{{ t(`live.startIssue.guidance.${issue.metric}`) }}</span></p>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <template #footer>
      <div v-if="slowProgress" class="issue-actions issue-actions--waiting">
        <button class="issue-details-toggle" :aria-expanded="detailsOpen" :aria-controls="detailsId" @click="toggleDetails">{{ t('live.startIssue.details') }}<ChevronDown aria-hidden="true" /></button>
        <el-button class="adjust-waiting" @click="adjust"><ScanLine aria-hidden="true" />{{ t('live.startIssue.adjust.exp') }}</el-button>
        <el-button @click="live.cancelSessionStart">{{ t('live.startIssue.cancel') }}</el-button>
      </div>
      <div v-else class="issue-actions">
        <el-button v-if="issue?.metric" type="primary" :disabled="live.isStarting" @click="adjust"><ScanLine aria-hidden="true" />{{ t(`live.startIssue.adjust.${issue.metric}`) }}</el-button>
        <el-button v-else-if="issue?.reason === 'capture-unavailable'" type="primary" :disabled="live.isStarting" @click="reconnect">{{ t('live.startIssue.chooseWindow') }}<ArrowRight aria-hidden="true" /></el-button>
        <el-button :loading="live.isStarting" :disabled="live.isStarting" @click="retry"><RefreshCw v-if="!live.isStarting" aria-hidden="true" />{{ live.isStarting ? t('live.startConfirming') : t('live.startIssue.retry') }}</el-button>
        <el-button text :disabled="live.isStarting" @click="live.dismissStartIssue">{{ t('live.startIssue.later') }}</el-button>
      </div>
    </template>
  </el-dialog>
</template>

<style scoped>
:global(.start-issue-overlay.el-overlay) { background: rgb(8 12 11 / 48%); }
:global(.start-issue-overlay .el-overlay-dialog) { display: flex; align-items: center; justify-content: center; padding: 0.75rem; }
:global(.start-issue-dialog.el-dialog) { width: min(31rem, calc(100vw - 1.5rem)) !important; max-height: calc(100dvh - 1.5rem); display: flex; flex-direction: column; margin: 0; padding: 0; overflow: hidden; border: 0.0625rem solid var(--border); border-radius: 1rem; background: var(--surface-raised); box-shadow: var(--shadow-raised); }
:global(.start-issue-dialog .el-dialog__header) { flex: none; margin: 0; padding: 1.5rem 1.5rem 0; }
:global(.start-issue-dialog .el-dialog__header.show-close) { padding-right: 2.75rem; }
:global(.start-issue-dialog .el-dialog__body) { min-height: 0; padding: 1rem 1.5rem 1.25rem; overflow-y: auto; overscroll-behavior: contain; }
:global(.start-issue-dialog .el-dialog__footer) { flex: none; padding: .875rem 1.5rem; border-top: .0625rem solid var(--border); background: var(--surface-hover); }
.issue-heading { display: grid; grid-template-columns: 2.75rem minmax(0, 1fr); align-items: center; gap: .875rem; animation: issue-content-in 240ms var(--ease-out); }
.issue-icon { position: relative; width: 2.75rem; height: 2.75rem; display: grid; place-items: center; flex: none; overflow: hidden; border: .0625rem solid color-mix(in srgb, var(--danger) 15%, transparent); border-radius: .875rem; color: var(--danger); background: color-mix(in srgb, var(--danger) 8%, var(--surface)); }
.issue-icon svg { width: 1.375rem; height: 1.375rem; }
.issue-icon.loading { color: var(--blue); background: var(--blue-soft); border-color: color-mix(in srgb, var(--blue) 15%, transparent); }
.issue-icon.loading::after { position: absolute; content: ''; width: 1.375rem; height: .125rem; top: .75rem; border-radius: 1rem; background: var(--blue); animation: issue-scan 2.4s ease-in-out infinite; }
.issue-eyebrow { display: block; color: var(--muted); font-size: var(--font-label); font-weight: 500; line-height: 1.4; }
.is-waiting .issue-eyebrow { color: var(--blue); }
.issue-heading h2 { margin: .25rem 0 0; color: var(--text-strong); font-size: 1.0625rem; font-weight: 700; line-height: 1.5; letter-spacing: -.015em; }
.issue-reason { margin: 0; color: var(--muted-strong); font-size: var(--font-body); line-height: 1.8; overflow-wrap: anywhere; }
.confirmation-progress { margin-top: 1rem; padding: .875rem 1rem; border: .0625rem solid color-mix(in srgb, var(--blue) 16%, var(--border)); border-radius: .625rem; background: color-mix(in srgb, var(--blue-soft) 50%, var(--surface-raised)); font-variant-numeric: tabular-nums; }
.confirmation-summary { display: flex; align-items: center; justify-content: space-between; gap: .75rem; color: var(--text); font-size: var(--font-body); font-weight: 600; line-height: 1.5; }
.confirmation-count { flex: none; color: var(--muted); font-size: var(--font-label); font-weight: 500; white-space: nowrap; }
.confirmation-count strong { display: inline-block; min-width: 1ch; margin-right: .25rem; color: var(--blue); font-size: 1.375rem; font-weight: 700; animation: issue-count-in 240ms var(--ease-out); }
.confirmation-steps { display: flex; gap: .375rem; margin-top: .625rem; }
.confirmation-steps > span { flex: 1; height: .25rem; overflow: hidden; border-radius: 1rem; background: color-mix(in srgb, var(--blue) 12%, var(--surface-subtle)); }
.confirmation-steps i { display: block; width: 100%; height: 100%; border-radius: inherit; background: var(--blue); transform: scaleX(0); transform-origin: left; transition: transform 320ms var(--ease-out); }
.confirmation-steps .complete i { transform: scaleX(1); }
.issue-details-toggle { display: inline-flex; align-items: center; justify-content: flex-start; gap: .375rem; min-height: 2.375rem; max-width: 100%; padding: .375rem .5rem; margin-left: -.5rem; border: 0; border-radius: .4375rem; background: transparent; color: var(--muted-strong); font: inherit; font-size: var(--font-label); line-height: 1.5; text-align: start; cursor: pointer; transition: background-color 160ms ease, color 160ms ease, transform 160ms var(--ease-out); }
.issue-details-toggle:hover { color: var(--blue); background: var(--blue-soft); }
.issue-details-toggle:active { transform: translateY(1px); }
.issue-details-toggle svg { width: .875rem; height: .875rem; flex: none; transition: transform 220ms var(--ease-out); }
.issue-details-toggle[aria-expanded='true'] svg { transform: rotate(180deg); }
.issue-details-toggle:focus-visible { box-shadow: 0 0 0 .125rem var(--blue); }
.issue-details-reveal { display: grid; grid-template-rows: 1fr; }
.issue-details-clip { min-height: 0; overflow: hidden; }
.issue-details { margin-top: 1rem; padding: .875rem; border: .0625rem solid var(--border); border-radius: .625rem; background: var(--surface-hover); }
.issue-details-toolbar { display: flex; align-items: center; justify-content: space-between; gap: .5rem; margin: -.25rem -.25rem .625rem 0; color: var(--text); font-size: var(--font-label); font-weight: 600; }
.issue-details-toolbar :deep(.el-button) { height: auto; min-height: 2rem; max-width: 65%; padding: .375rem .5rem; color: var(--muted-strong); }
.issue-details-toolbar :deep(.el-button > span) { white-space: normal; line-height: 1.4; }
.issue-details-toolbar :deep(svg) { width: .875rem; height: .875rem; flex: none; margin-right: .375rem; }
.details-refresh-icon { animation: issue-refresh 480ms var(--ease-out); }
.issue-diagnostics { animation: issue-content-in 200ms var(--ease-out); }
.issue-preview-empty { margin: .75rem 0 0; padding: 1rem .75rem; border: .0625rem dashed var(--border-strong); border-radius: .375rem; background: var(--surface-raised); color: var(--muted); font-size: var(--font-label); line-height: 1.6; text-align: center; }
.review-note { margin: 0.5rem 0 0; color: var(--muted); font-size: var(--font-label); line-height: 1.5; }
.issue-preview { margin-top: 0.875rem; }
.ocr-evidence { margin-top: 0.75rem; padding: 0.625rem 0.75rem; border: .0625rem solid var(--border); border-radius: 0.375rem; background: var(--surface-raised); }
.ocr-evidence span { display: block; color: var(--muted); font-size: var(--font-caption); }
.ocr-evidence code { display: block; margin-top: 0.25rem; overflow-wrap: anywhere; color: var(--text-strong); font-size: var(--font-label); line-height: 1.6; white-space: pre-wrap; }
.recognition-path { margin: 0.5rem 0 0; color: var(--muted); font-size: var(--font-caption); }
.issue-guidance { margin: 0.75rem 0 0; display: grid; grid-template-columns: auto minmax(0, 1fr); align-items: start; gap: 0.5rem; color: var(--text); font-size: var(--font-label); line-height: 1.5; }
.issue-guidance svg { width: 1rem; height: 1rem; margin-top: 0.1875rem; color: var(--blue); }
.issue-actions { display: flex; justify-content: flex-end; flex-wrap: wrap; gap: 0.5rem; }
.issue-actions--waiting { align-items: center; justify-content: space-between; }
.issue-actions :deep(.el-button) { margin: 0; height: auto; min-height: 2.375rem; padding: .5rem .875rem; border-radius: .5rem; font-size: var(--font-label); transition: background-color 160ms ease, border-color 160ms ease, color 160ms ease, transform 160ms var(--ease-out), box-shadow 160ms ease; }
.issue-actions :deep(.el-button:active) { transform: translateY(1px); }
.issue-actions :deep(.el-button > span) { white-space: normal; line-height: 1.25; }
.issue-actions :deep(svg) { width: 1rem; height: 1rem; }
.issue-actions :deep(.el-button:focus-visible), .issue-details-toolbar :deep(.el-button:focus-visible) { box-shadow: 0 0 0 .125rem var(--blue); }
/* Keep this dialog's interaction motion independent of the system motion preference. */
.issue-disclosure-enter-active, .issue-disclosure-leave-active { transition: grid-template-rows 260ms var(--ease-out), opacity 180ms ease; }
.issue-disclosure-enter-from, .issue-disclosure-leave-to { grid-template-rows: 0fr; opacity: 0; }
:global(.start-issue-motion-enter-active) { animation: issue-overlay-in 240ms ease both; }
:global(.start-issue-motion-leave-active) { animation: issue-overlay-in 180ms ease reverse both; }
:global(.start-issue-motion-enter-active .start-issue-dialog) { animation: issue-dialog-in 280ms cubic-bezier(.2, .8, .2, 1) both; }
:global(.start-issue-motion-leave-active .start-issue-dialog) { animation: issue-dialog-in 180ms ease reverse both; }
@keyframes issue-scan { 0%, 100% { transform: translateY(0); opacity: .25; } 50% { transform: translateY(1.125rem); opacity: .8; } }
@keyframes issue-refresh { from { transform: rotate(-180deg); } to { transform: rotate(0); } }
@keyframes issue-count-in { from { opacity: .4; transform: translateY(.2rem); } to { opacity: 1; transform: translateY(0); } }
@keyframes issue-content-in { from { opacity: 0; transform: translateY(.25rem); } to { opacity: 1; transform: translateY(0); } }
@keyframes issue-overlay-in { from { opacity: 0; } to { opacity: 1; } }
@keyframes issue-dialog-in { from { opacity: 0; transform: translateY(.625rem) scale(.985); } to { opacity: 1; transform: translateY(0) scale(1); } }
@media (max-width: 31rem) {
  :global(.start-issue-dialog .el-dialog__header) { padding: 1rem 1rem 0; }
  :global(.start-issue-dialog .el-dialog__body) { padding: .875rem 1rem 1rem; }
  :global(.start-issue-dialog .el-dialog__footer) { padding: .75rem 1rem; }
  .issue-heading { gap: .75rem; }
  .issue-heading h2 { font-size: 1rem; }
  .issue-actions:not(.issue-actions--waiting) { display: grid; }
  .issue-actions--waiting { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); }
  .issue-actions--waiting > .el-button { justify-self: end; max-width: 100%; }
}
</style>
